import { Crown, ShieldCheck, Sparkles, UserRound } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth-context.js'
import { useLanguage } from '../contexts/LanguageContext.jsx'

const COPY = {
  zh: {
    currentIdentity: '当前身份',
    admin: '管理员',
    freeUser: '普通用户',
    vipMember: 'VIP 会员',
    monthly: '月卡 VIP',
    quarterly: '季卡 VIP',
    yearly: '年卡 VIP',
    unset: '暂未设置',
    adminNote: '管理员不受次数限制。',
    currentPlan: '当前套餐：{{plan}}',
    validPeriod: '有效期：{{period}}',
    remainingDays: '剩余：{{days}} 天',
    freeNote: '可使用基础额度，适合先体验简历和项目能力。',
    upgradeNote: '升级 VIP 后可获得更高额度和更多增强能力。',
    usageEmpty: '当前暂无额度统计。',
    freeQuota: '免费额度按月统计，用完后可升级 VIP 获得更多额度。',
    viewVip: '查看 VIP 权益',
    unlimited: '不限额度',
    remaining: '{{period}}剩余 {{remaining}} / {{limit}}',
    today: '今日',
    month: '本月',
    benefitNote: 'VIP 额度更适合持续优化项目、简历和面试准备。',
    benefits: ['简历优化增强', 'AI 项目分析增强', '面试助手', '导出功能'],
    labels: {
      resume_analyze: '简历分析',
      resume_draft: '初版简历生成',
      resume_optimize: '简历优化建议',
      resume_export: '简历导出',
      github_analysis: 'GitHub 项目分析',
      ai_chat: 'AI 聊天',
    },
  },
  en: {
    currentIdentity: 'Current plan',
    admin: 'Admin',
    freeUser: 'Free user',
    vipMember: 'VIP member',
    monthly: 'Monthly VIP',
    quarterly: 'Quarterly VIP',
    yearly: 'Yearly VIP',
    unset: 'Not set',
    adminNote: 'Admins are not limited by usage quotas.',
    currentPlan: 'Plan: {{plan}}',
    validPeriod: 'Valid through: {{period}}',
    remainingDays: '{{days}} days left',
    freeNote: 'Basic quota is available for trying resume and project features.',
    upgradeNote: 'Upgrade to VIP for higher quota and enhanced capabilities.',
    usageEmpty: 'No usage summary yet.',
    freeQuota: 'Free quota resets monthly. Upgrade to VIP for more capacity.',
    viewVip: 'View VIP benefits',
    unlimited: 'Unlimited',
    remaining: '{{remaining}} / {{limit}} left {{period}}',
    today: 'today',
    month: 'this month',
    benefitNote: 'VIP quota is better for ongoing project, resume, and interview work.',
    benefits: ['Enhanced resume optimization', 'Enhanced AI project analysis', 'Interview assistant', 'Export features'],
    labels: {
      resume_analyze: 'Resume analysis',
      resume_draft: 'Resume draft',
      resume_optimize: 'Resume optimization',
      resume_export: 'Resume export',
      github_analysis: 'GitHub project analysis',
      ai_chat: 'AI chat',
    },
  },
}

function interpolate(template, values = {}) {
  return String(template).replace(/\{\{(\w+)\}\}/g, (_, key) => values[key] ?? '')
}

function formatDate(value, language) {
  const copy = COPY[language] || COPY.zh
  if (!value) return copy.unset
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10)
  return new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'zh-CN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date)
}

function daysLeft(value) {
  if (!value) return 0
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 0
  return Math.max(0, Math.ceil((date.getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
}

function planLabel(value, copy) {
  const text = String(value || '').trim()
  if (text === 'monthly') return copy.monthly
  if (text === 'quarterly') return copy.quarterly
  if (text === 'yearly') return copy.yearly
  if (!text) return copy.vipMember
  return text.includes('VIP') ? text : `${text} VIP`
}

function usageText(item, isVip, copy) {
  if (!item) return copy.usageEmpty
  if (item.limit === null || item.limit === undefined) return copy.unlimited
  const period = item.period === 'day' ? copy.today : item.period === 'month' ? copy.month : (isVip ? copy.today : copy.month)
  return interpolate(copy.remaining, { period, remaining: item.remaining ?? 0, limit: item.limit })
}

function MembershipStatusCard({ className = '', membershipOverride = null }) {
  const { user } = useAuth() || {}
  const { language } = useLanguage()
  const copy = COPY[language] || COPY.zh
  const membership = membershipOverride || user?.membership || null
  if (!user && !membership) return null

  const usageSummary = user?.usageSummary || null
  const isAdmin = membership?.isAdmin || user?.isAdmin || user?.is_admin
  const isVip = isAdmin || membership?.isVip || membership?.tier === 'vip'
  const tierLabel = isAdmin ? copy.admin : isVip ? planLabel(membership?.planName || membership?.planId || membership?.plan, copy) : copy.freeUser
  const Icon = isAdmin ? ShieldCheck : isVip ? Crown : UserRound

  return (
    <aside className={`membership-status-card ${isVip ? 'is-vip' : ''} ${isAdmin ? 'is-admin' : ''} ${className}`}>
      <header>
        <span><Icon size={18} /></span>
        <div>
          <small>{copy.currentIdentity}</small>
          <strong>{tierLabel}</strong>
        </div>
      </header>
      {isAdmin ? (
        <p className="membership-status-note">{copy.adminNote}</p>
      ) : isVip ? (
        <>
          <p className="membership-status-note">
            {interpolate(copy.currentPlan, { plan: planLabel(membership?.planName || membership?.planId || membership?.plan, copy) })}
          </p>
          <p className="membership-status-note">
            {interpolate(copy.validPeriod, {
              period: `${membership?.startsAt ? `${formatDate(membership.startsAt, language)} - ` : ''}${formatDate(membership?.expiresAt, language)}`,
            })}
          </p>
          <p className="membership-status-note">
            {interpolate(copy.remainingDays, { days: daysLeft(membership?.expiresAt) })}
          </p>
          <div className="membership-usage-grid">
            {copy.benefits.map((benefit) => (
              <div key={benefit}>
                <span>✓ {benefit}</span>
              </div>
            ))}
          </div>
          {usageSummary && (
            <div className="membership-usage-grid">
              {Object.entries(copy.labels).map(([key, label]) => (
                usageSummary[key] ? (
                  <div key={key}>
                    <span>{label}</span>
                    <strong>{usageText(usageSummary[key], true, copy)}</strong>
                  </div>
                ) : null
              ))}
            </div>
          )}
          <p className="membership-status-note"><Sparkles size={14} /> {copy.benefitNote}</p>
        </>
      ) : (
        <>
          <p className="membership-status-note">{copy.freeNote}</p>
          <p className="membership-status-note">{copy.upgradeNote}</p>
          {usageSummary ? (
            <div className="membership-usage-grid">
              {Object.entries(copy.labels).map(([key, label]) => (
                usageSummary[key] ? (
                  <div key={key}>
                    <span>{label}</span>
                    <strong>{usageText(usageSummary[key], false, copy)}</strong>
                  </div>
                ) : null
              ))}
            </div>
          ) : (
            <p className="membership-status-note">{copy.usageEmpty}</p>
          )}
          <p className="membership-status-note">{copy.freeQuota}</p>
          <Link className="secondary-button membership-status-action" to="/vip">
            <Crown size={16} />
            {copy.viewVip}
          </Link>
        </>
      )}
    </aside>
  )
}

export default MembershipStatusCard
